// src/features/services/media/hooks/mixEngine/orchestrator/coldStart.ts
import type React from 'react';
import { disconnectAll } from './graphBuilder';
import { makeTransportGate } from './helpers/transportGate';
import { stopAllElements } from './helpers/stopAllElements';
import { getCtx } from '../ctxSingleton';
import { userGestureSeen, resumeCtxOnGesture } from './autoplayGate';

/**
 * Tear everything down so the next render can build a fresh graph.
 * - Soft cold start (default): reuse AudioContext, but dispose graphs, worklets, buffers, and refs
 * - Hard cold start: also close() the AudioContext and create a new one (modules must be reloaded)
 */
export async function coldStart(
  refs: any,
  opts: {
    hard?: boolean;
    bufferModeRef: React.MutableRefObject<boolean>;
    scheduledRef: React.MutableRefObject<Record<string, any> | null>;
    disposeScheduled: (s: Record<string, any>) => void;
    stopAll: (s: Record<string, any>) => void;
    pauseAll: () => void;
    setStatus: (s: 'idle' | 'loading' | 'playing' | 'paused') => void;
    setCurrentTime: (t: number) => void;
  }
): Promise<void> {
  const {
    hard = false,
    bufferModeRef,
    scheduledRef,
    disposeScheduled,
    stopAll,
    pauseAll,
    setStatus,
    setCurrentTime,
  } = opts;

  const ctx: AudioContext | null = refs?.audioCtxRef?.current || null;

  // Yield one frame to allow the UI to flush (e.g., stop button ripple) before heavy teardown.
  // This helps prevent apparent UI freezes when stopping or swapping scopes.
  try {
    await new Promise<void>((resolve) => {
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => resolve());
      } else {
    // Use shared gate utility
    try { makeTransportGate(refs).closeTransportGate(); } catch {}
      }
    });
  } catch {}

  // 0) Close transport gate immediately (prevents ghost overlap)
  try {
    const g: GainNode['gain'] | undefined = refs?.gateGainRef?.current?.gain;
    if (g) {
      if (ctx) {
        const now = ctx.currentTime;
    // Belt & suspenders: also hard-mute master gain immediately
    try {
      const mg: GainNode['gain'] | undefined = refs?.masterGainRef?.current?.gain;
      if (mg) {
        if (ctx) {
          const now = ctx.currentTime;
          try { mg.cancelScheduledValues(now); } catch {}
          try { mg.setValueAtTime(0, now); } catch { try { (mg as any).value = 0; } catch {} }
        } else {
          try { (mg as any).value = 0; } catch {}
        }
      }
    } catch {}
    // Proactively disconnect any bypass/tee paths that could reach destination
    try { refs.bypassGainRef?.current?.disconnect?.(); } catch {}
    try { refs.teeGainRef?.current?.disconnect?.(); } catch {}
        try { g.cancelScheduledValues(now); } catch {}
        try { g.setValueAtTime(0, now); } catch { try { (g as any).value = 0; } catch {} }
      } else {
        try { (g as any).value = 0; } catch {}
      }
    }
  } catch {}

  // 1) Stop anything that can produce audio
  try { pauseAll(); } catch {}
  // 1a) Aggressively stop and drain any HTMLAudioElements to avoid direct system playback
  try { stopAllElements(refs?.elsRef?.current || {}); } catch {}
  try { if (bufferModeRef.current && scheduledRef.current) stopAll(scheduledRef.current); } catch {}
  try { if (scheduledRef.current) disposeScheduled(scheduledRef.current); } catch {}
  scheduledRef.current = null;

  // 1b) Reset probe/heartbeat guards so the next scope starts clean
  try { (refs as any)._gateProbeActive = false; } catch {}
  try { (refs as any)._gateProbeOwnerId = undefined; } catch {}
  try { (refs as any)._gateProbeLastDb = null; } catch {}
  try { (refs as any)._gateProbeLastLogTs = 0; } catch {}
  try { (refs as any)._postProbeId = 0; } catch {}
  try { (refs as any)._silentStarts = 0; } catch {}
  try { (refs as any)._hbId = 0; } catch {}
  // clear heartbeat interval and meters
  try { if ((refs as any)._hbTimer) { clearInterval((refs as any)._hbTimer); (refs as any)._hbTimer = 0; } } catch {}
  try {
    const m = (refs as any)._hbMeters || {};
    Object.values(m || {}).forEach((n: any) => { try { n.disconnect(); } catch {} });
    (refs as any)._hbMeters = null;
  } catch {}

  // 2) Disconnect entire graph (stems + master)
  try {
    const stems = Object.keys(refs?.gainsRef?.current || {}).map(role => ({
      role,
      src: refs?.sourcesRef?.current?.[role] ?? null,
      delay: refs?.delaysRef?.current?.[role] ?? null,
      gain: refs?.gainsRef?.current?.[role] ?? null,
      analyser: refs?.analyzersRef?.current?.[role] ?? null,
    }));
    const master = refs?.masterGainRef?.current && refs?.masterFilterRef?.current && refs?.masterHighpassRef?.current
      ? {
          masterGain: refs.masterGainRef.current as GainNode,
          hp: refs.masterHighpassRef.current as BiquadFilterNode,
          lp: refs.masterFilterRef.current as BiquadFilterNode,
          analyser: (refs.masterAnalyserRef?.current ?? null) as AnalyserNode | null,
        }
      : null;
    try { disconnectAll(master as any, stems as any); } catch {}
  } catch {}

  // 2b) Element-mode leftovers: disconnect and clear any element refs
  try {
    const els = refs?.connectedElsRef?.current || {};
    for (const k of Object.keys(els)) {
      try { (els[k] as any)?.disconnect?.(); } catch {}
    }
    if (refs?.connectedElsRef) refs.connectedElsRef.current = {};
  } catch {}

  // 3) Clear all live refs / maps
  try { refs.sourcesRef.current = {}; } catch {}
  try { refs.gainsRef.current = {}; } catch {}
  try { refs.delaysRef.current = {}; } catch {}
  try { refs.analyzersRef.current = {}; } catch {}
  // Drop master/stems containers to release large object graphs quickly
  try { refs.stems = []; } catch {}
  try { refs.masterGraph = null; } catch {}
  // Do not reset the gate's gain here; we preserve previous target so the opener restores it.
  try { refs.connectedElsRef && (refs.connectedElsRef.current = {} as any); } catch {}
  try { refs.masterAnalyserRef.current = null; } catch {}
  try { refs.masterFilterRef.current = null; } catch {}
  try { refs.masterHighpassRef.current = null; } catch {}
  try { refs.masterGainRef.current = null; } catch {}
  try { refs.transportClockRef && (refs.transportClockRef.current = null); } catch {}
  try { refs.rbTempoRef && (refs.rbTempoRef.current = null); } catch {}

  // 4) Clear decoded buffers & metadata signatures you keep around
  try { refs.buffersRef && (refs.buffersRef.current = null); } catch {}
  try { refs.buffersSigRef && (refs.buffersSigRef.current = ''); } catch {}
  try { refs.scheduledMetaRef && (refs.scheduledMetaRef.current = null); } catch {}

  // 5) Reset UI face
  try { setCurrentTime(0); } catch {}
  try { setStatus('paused'); } catch {}

  // 6) Optional hard reset of context, else micro suspend/resume to flush tails
  if (hard && ctx) {
    try { await ctx.suspend().catch(() => {}); } catch {}
    try { await ctx.close().catch(() => {}); } catch {}
    // Give the browser a breath so the old AW GlobalScope can actually tear down
    try { await new Promise<void>(r => setTimeout(r, 150)); } catch {}
    try {
      // Create/retrieve context via singleton to enforce single-context policy
      refs.audioCtxRef.current = null;
      refs.audioCtxRef.current = getCtx();
      // Immediately resume the fresh context only if a user gesture already occurred
      const fresh = refs.audioCtxRef.current as AudioContext;
      try {
        if (userGestureSeen()) { await fresh.resume(); }
        else { void resumeCtxOnGesture(fresh); }
      } catch {}
    } catch {}
  } else if (ctx) {
    // Some browsers benefit from a micro suspend/resume to flush zombie tails.
    // Avoid resume() pre-gesture to prevent autoplay warnings.
    try { await ctx.suspend().catch(() => {}); } catch {}
    try {
      if (userGestureSeen()) { await ctx.resume().catch(() => {}); }
      else { void resumeCtxOnGesture(ctx); }
    } catch {}
  }
}

export default coldStart;
